import { GiftRecord, GiftStatistics, AddGiftRecordRequest, UpdateGiftRecordRequest, BatchGiftEntry, BatchGiftRequest, GiftType } from '../../types/invitation';
import { supabase } from './client';
import { transformDBGiftRecordToApp } from './transforms';
import { createLogger } from '../logger';

const giftLog = createLogger('gift');

export const giftService = {
  async addGiftRecord(request: AddGiftRecordRequest): Promise<string> {
    giftLog.debug('新增紅包記錄', request);

    const { data, error } = await supabase.rpc('add_gift_record', {
      p_invitation_id: request.invitationId,
      p_rsvp_id: request.rsvpId || null,
      p_guest_name: request.guestName,
      p_guest_email: request.guestEmail || null,
      p_amount: request.amount,
      p_currency: request.currency || 'TWD',
      p_gift_type: request.giftType || 'cash',
      p_gift_description: request.giftDescription || null,
      p_message: request.message || null,
    });

    if (error) {
      giftLog.error('新增紅包記錄失敗:', error);
      throw new Error(`新增紅包記錄失敗: ${error.message}`);
    }

    giftLog.info('成功新增記錄', data);
    return data;
  },

  async getGiftRecordsByInvitation(invitationId: string): Promise<GiftRecord[]> {
    giftLog.debug('查詢紅包記錄', invitationId);

    const { data, error } = await supabase.rpc('get_gift_records_by_invitation', {
      p_invitation_id: invitationId
    });

    if (error) {
      giftLog.error('查詢紅包記錄失敗:', error);
      throw new Error(`查詢紅包記錄失敗: ${error.message}`);
    }

    giftLog.info('成功取得', data.length, '筆記錄');
    return data.map(transformDBGiftRecordToApp);
  },

  async updateGiftRecord(request: UpdateGiftRecordRequest): Promise<void> {
    giftLog.debug('更新紅包記錄', request);

    const { error } = await supabase.rpc('update_gift_record', {
      p_record_id: request.recordId,
      p_guest_name: request.guestName || null,
      p_guest_email: request.guestEmail || null,
      p_amount: request.amount || null,
      p_currency: request.currency || null,
      p_gift_type: request.giftType || null,
      p_gift_description: request.giftDescription || null,
      p_message: request.message || null,
    });

    if (error) {
      giftLog.error('更新紅包記錄失敗:', error);
      throw new Error(`更新紅包記錄失敗: ${error.message}`);
    }

    giftLog.info('成功更新記錄', request.recordId);
  },

  async deleteGiftRecord(recordId: string): Promise<void> {
    giftLog.debug('刪除紅包記錄', recordId);

    const { error } = await supabase.rpc('delete_gift_record', {
      p_record_id: recordId
    });

    if (error) {
      giftLog.error('刪除紅包記錄失敗:', error);
      throw new Error(`刪除紅包記錄失敗: ${error.message}`);
    }

    giftLog.info('成功刪除記錄', recordId);
  },

  async getGiftStatistics(invitationId: string): Promise<GiftStatistics> {
    giftLog.debug('查詢紅包統計', invitationId);

    const { data, error } = await supabase.rpc('get_gift_statistics', {
      p_invitation_id: invitationId
    });

    if (error) {
      giftLog.error('查詢紅包統計失敗:', error);
      throw new Error(`查詢紅包統計失敗: ${error.message}`);
    }

    giftLog.info('成功取得統計資料', data);

    const stats: GiftStatistics = {
      totalAmount: Number(data.totalAmount) || 0,
      totalRecords: Number(data.totalRecords) || 0,
      averageAmount: Number(data.averageAmount) || 0,
      guestSideStats: {
        bride: {
          totalAmount: Number(data.guestSideStats?.bride?.totalAmount) || 0,
          count: Number(data.guestSideStats?.bride?.count) || 0
        },
        groom: {
          totalAmount: Number(data.guestSideStats?.groom?.totalAmount) || 0,
          count: Number(data.guestSideStats?.groom?.count) || 0
        }
      },
      currencyBreakdown: data.currencyBreakdown || {},
      giftTypeBreakdown: data.giftTypeBreakdown || {}
    };

    return stats;
  },

  async exportGiftRecordsToCSV(invitationId: string): Promise<string> {
    giftLog.debug('匯出紅包記錄', invitationId);

    const records = await this.getGiftRecordsByInvitation(invitationId);

    const headers = [
      '賓客姓名',
      '電子郵件',
      '金額',
      '幣別',
      '紅包類型',
      '禮品描述',
      '留言',
      '記錄時間'
    ];

    const rows = records.map(record => [
      record.guestName,
      record.guestEmail || '',
      record.amount.toString(),
      record.currency,
      record.giftType === 'cash' ? '現金' : record.giftType === 'gift' ? '禮品' : '現金+禮品',
      record.giftDescription || '',
      record.message || '',
      record.recordedAt.toLocaleString('zh-TW')
    ]);

    const csvContent = [headers, ...rows]
      .map(row => row.map(cell => `"${cell.replace(/"/g, '""')}"`).join(','))
      .join('\n');

    giftLog.info('成功生成 CSV', records.length, '筆記錄');
    return csvContent;
  },

  async getRSVPGuestsWithGiftStatus(invitationId: string): Promise<BatchGiftEntry[]> {
    giftLog.debug('查詢 RSVP 賓客和紅包狀態', invitationId);

    try {
      const { data, error } = await supabase.rpc('get_rsvp_guests_with_gift_status', {
        p_invitation_id: invitationId
      });

      if (error) throw error;

      const entries: BatchGiftEntry[] = (data || []).map((row: any) => {
        return {
          rsvpId: row.rsvp_id,
          guestName: row.guest_name,
          guestEmail: row.guest_email,
          guestSide: row.guest_side as 'bride' | 'groom',
          attendingStatus: row.attending as 'yes' | 'no' || 'yes',
          guestCount: row.guest_count || 1,
          amount: row.existing_gift_amount ? Number(row.existing_gift_amount) : 0,
          currency: row.existing_gift_currency || 'TWD',
          giftType: (row.existing_gift_type as GiftType) || 'cash',
          message: row.existing_gift_message || '',
          hasExistingRecord: !!row.existing_gift_id,
          existingRecordId: row.existing_gift_id
        };
      });

      giftLog.info('成功取得', entries.length, '筆 RSVP 資料');
      return entries;

    } catch (error) {
      giftLog.error('查詢 RSVP 賓客和紅包狀態失敗', error);
      throw error;
    }
  },

  async processBatchGiftRecords(request: BatchGiftRequest): Promise<void> {
    giftLog.debug('批量處理紅包記錄', request.entries.length, '筆');

    try {
      const results = [];

      for (const entry of request.entries) {
        if (entry.amount <= 0) continue;

        try {
          if (entry.hasExistingRecord && entry.existingRecordId) {
            const { error } = await supabase.rpc('update_gift_record', {
              p_record_id: entry.existingRecordId,
              p_guest_name: entry.guestName,
              p_guest_email: entry.guestEmail || null,
              p_amount: entry.amount,
              p_currency: entry.currency,
              p_gift_type: entry.giftType,
              p_message: entry.message || null
            });

            if (error) throw error;
            results.push({ type: 'updated', guestName: entry.guestName });

          } else {
            const { error } = await supabase.rpc('add_gift_record', {
              p_invitation_id: request.invitationId,
              p_rsvp_id: entry.rsvpId,
              p_guest_name: entry.guestName,
              p_guest_email: entry.guestEmail || null,
              p_amount: entry.amount,
              p_currency: entry.currency,
              p_gift_type: entry.giftType,
              p_message: entry.message || null
            });

            if (error) throw error;
            results.push({ type: 'added', guestName: entry.guestName });
          }
        } catch (entryError) {
          giftLog.error(`處理 ${entry.guestName} 的紅包記錄失敗:`, entryError);
          results.push({ type: 'error', guestName: entry.guestName, error: entryError });
        }
      }

      const successful = results.filter(r => r.type !== 'error');
      const failed = results.filter(r => r.type === 'error');

      giftLog.info('批量處理完成', {
        total: request.entries.length,
        successful: successful.length,
        failed: failed.length
      });

      if (failed.length > 0) {
        const failedNames = failed.map(f => f.guestName).join(', ');
        throw new Error(`部分記錄處理失敗: ${failedNames}`);
      }

    } catch (error) {
      giftLog.error('批量處理失敗', error);
      throw error;
    }
  }
};
